import { useBrand } from '@/config/brands';
import { type FraudCategory } from '@/config/fraudCategories';
import * as LucideIcons from 'lucide-react';
import { Star } from 'lucide-react';

// Patriotic palette
const NAVY = '#002868';
const GOLD = '#C5A55A';

interface CategoryOverviewPanelProps {
  categories: FraudCategory[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  hospitalCountByCategory?: Record<string, number>;
}

export const CategoryOverviewPanel = ({
  categories,
  selectedId,
  onSelect,
  hospitalCountByCategory = {},
}: CategoryOverviewPanelProps) => {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      {categories.map((cat) => {
        const isSelected = selectedId === cat.id;
        const Icon = (LucideIcons as any)[cat.icon] || LucideIcons.AlertTriangle;
        const hospCount = hospitalCountByCategory[cat.id] ?? null;

        return (
          <button
            key={cat.id}
            onClick={() => onSelect(cat.id)}
            className="relative text-left rounded-xl transition-all duration-200"
            style={isAhp ? {
              padding: '1rem',
              backgroundColor: isSelected ? 'rgba(0,40,104,0.04)' : 'rgba(255,255,255,0.95)',
              border: isSelected ? `2px solid ${GOLD}` : '1px solid rgba(0,0,0,0.08)',
              boxShadow: isSelected
                ? `0 4px 16px rgba(0,40,104,0.12), inset 0 0 0 1px ${GOLD}40`
                : '0 1px 4px rgba(0,0,0,0.04)',
            } : {
              padding: '1rem',
              backgroundColor: isSelected ? 'rgba(0,255,65,0.05)' : '#1a1a2e',
              border: isSelected ? '1px solid #00ff41' : '1px solid rgba(255,255,255,0.1)',
              boxShadow: isSelected ? '0 0 12px rgba(0,255,65,0.15)' : 'none',
            }}
          >
            <div className="flex items-start gap-3">
              <div
                className="p-2 rounded-lg shrink-0"
                style={isAhp ? {
                  backgroundColor: `${NAVY}10`,
                  border: `1px solid ${NAVY}15`,
                } : {
                  backgroundColor: `${cat.color}20`,
                }}
              >
                <Icon
                  className="w-5 h-5"
                  style={{ color: isAhp ? NAVY : cat.color }}
                />
              </div>
              <div className="min-w-0">
                <h3
                  className="font-semibold text-sm leading-tight"
                  style={isAhp ? {
                    fontFamily: "'Instrument Serif', Georgia, serif",
                    color: '#1B1B1B',
                  } : {
                    color: '#f0f0f0',
                  }}
                >
                  {cat.shortName}
                </h3>
                <p
                  className="text-xs mt-1 line-clamp-2"
                  style={{ color: isAhp ? 'rgba(27,27,27,0.62)' : 'rgba(255,255,255,0.4)' }}
                >
                  {cat.description}
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <span
                    className="text-xs font-mono"
                    style={{ color: isAhp ? 'rgba(27,27,27,0.35)' : 'rgba(255,255,255,0.35)' }}
                  >
                    {cat.codes.length} codes
                  </span>
                  {hospCount !== null && (
                    <span
                      className="text-xs font-mono"
                      style={{ color: isAhp ? 'rgba(27,27,27,0.35)' : 'rgba(255,255,255,0.35)' }}
                    >
                      {hospCount} hospitals
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Selected indicator — gold accent bar + star */}
            {isSelected && isAhp && (
              <>
                <div
                  className="absolute top-0 left-0 w-1 h-full rounded-l-xl"
                  style={{ backgroundColor: GOLD }}
                />
                <div className="absolute top-2 right-2">
                  <Star className="w-3.5 h-3.5" style={{ color: GOLD, fill: GOLD }} />
                </div>
              </>
            )}
            {isSelected && !isAhp && (
              <div
                className="absolute top-0 left-0 w-1 h-full rounded-l-lg"
                style={{ backgroundColor: '#00ff41' }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
};
